import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { block, clean, drum, rod } from './models.js';

/**
 * Every small thing in the catalogue, as low-poly geometry built in code.
 *
 * A keg, a suitcase, an orange, a fire extinguisher, a scorpion: each is drawn
 * as the thing it is, sized to the box the game already gives it (`radius` and
 * `height` on its catalogue row, handed in here as metres) and standing on
 * y = 0 inside it. Nothing here is collided with and nothing here reads the
 * game: a kind, a width, a height and a colour in, a geometry out.
 *
 * Colours are baked into the vertices, so one thing is one draw call however
 * many parts it has; glass is the second group, on a see-through material.
 * The row's colour is the thing's main colour; the rest are the colours those
 * things are.
 *
 *   propGeometry(kind, W, H, colour)  one geometry, groups [solid, glass], or
 *                                     null for a kind with no model
 */

const METAL = 0x9aa0a6;
const DARK = 0x222326;
const WHITE = 0xf2f0ea;
const GLASS = 0xcfe0e6;
const WOOD = 0x8a6a44;
const BRASS = 0xc9a35b;
const BLACK = 0x151515;

const lathe = (points, segments = 12) => new THREE.LatheGeometry(points.map(([r, y]) => new THREE.Vector2(Math.max(0.0005, r), y)), segments);
const ball = (r, x = 0, y = 0, z = 0, sx = 1, sy = 1, sz = 1, segments = 10) =>
  new THREE.SphereGeometry(r, segments, Math.max(4, Math.round(segments * 0.6))).scale(sx, sy, sz).translate(x, y, z);
/**
 * A ring, built at the origin in the xy plane, turned (`spin` about z first,
 * then `tip` about x, then `yaw` about y) and only then moved into place.
 */
const ring = (R, r, x = 0, y = 0, z = 0, { arc = Math.PI * 2, spin = 0, tip = 0, yaw = 0, segments = 12 } = {}) =>
  new THREE.TorusGeometry(R, r, 5, segments, arc).rotateZ(spin).rotateX(tip).rotateY(yaw).translate(x, y, z);
/** A ring lying flat, at a height. */
const flatRing = (R, r, x, y, z, options = {}) => ring(R, r, x, y, z, { ...options, tip: Math.PI / 2 });
/** A cone pointing along +y, turned (`spin` about z, `yaw` about y) and moved. */
const cone = (r, h, x = 0, y = 0, z = 0, segments = 8, { spin = 0, yaw = 0 } = {}) =>
  new THREE.ConeGeometry(r, h, segments).rotateZ(spin).rotateY(yaw).translate(x, y, z);

/** A model under construction: parts, each with its colour, solid or glass. */
function kit() {
  const solid = [];
  const glass = [];
  return {
    solid,
    glass,
    add(geometry, colour) {
      solid.push([geometry, colour]);
      return geometry;
    },
    clear(geometry, colour = GLASS) {
      glass.push([geometry, colour]);
      return geometry;
    },
  };
}

/** Builders, each `(k, W, H, c)`: a kit, the width and height in metres, the row's colour. */
const BUILD = {
  // --- glass and bottles ---------------------------------------------------------
  glass(k, W, H) {
    const r = W / 2;
    k.clear(lathe([[r * 0.7, 0], [r * 0.72, H * 0.06], [r * 0.95, H], [r * 0.9, H], [r * 0.64, H * 0.08], [0, H * 0.08]]));
    k.clear(drum(r * 0.66, r * 0.7, H * 0.4, 0, H * 0.25, 0, 10), 0xdde8ee);
  },
  wineglass(k, W, H) {
    const r = W / 2;
    k.clear(lathe([[r * 0.8, 0], [r * 0.8, H * 0.02], [r * 0.08, H * 0.05], [r * 0.08, H * 0.45], [r * 0.55, H * 0.55], [r, H * 0.8], [r * 0.85, H], [r * 0.8, H], [r * 0.9, H * 0.8], [r * 0.08, H * 0.5]]));
    k.clear(ball(r * 0.6, 0, H * 0.62, 0, 1, 0.5, 1, 8), 0x7a1e2e);
  },
  bottle(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.9, 0], [r, H * 0.02], [r, H * 0.6], [r * 0.4, H * 0.78], [r * 0.3, H * 0.95], [r * 0.34, H], [0, H]]), 0x1e3a28);
    k.add(drum(r * 1.01, r * 1.01, H * 0.22, 0, H * 0.35, 0, 12), 0xe8e0c8);
    k.add(drum(r * 0.36, r * 0.36, H * 0.1, 0, H * 0.94, 0, 8), c);
  },
  carafe(k, W, H) {
    const r = W / 2;
    k.clear(lathe([[r * 0.7, 0], [r, H * 0.15], [r, H * 0.5], [r * 0.45, H * 0.8], [r * 0.5, H], [r * 0.4, H], [0, H * 0.1]]));
    k.clear(drum(r * 0.9, r * 0.7, H * 0.4, 0, H * 0.25, 0, 10), 0xdde8ee);
  },
  saltshaker(k, W, H, c) {
    const r = W / 2;
    k.clear(lathe([[r * 0.9, 0], [r, H * 0.1], [r * 0.85, H * 0.75], [0, H * 0.75]]));
    k.add(drum(r * 0.7, r * 0.7, H * 0.55, 0, H * 0.33, 0, 8), c);
    k.add(lathe([[r * 0.85, H * 0.75], [r * 0.8, H * 0.92], [r * 0.4, H], [0, H]]), METAL);
  },
  salt(k, W, H) {
    const r = W / 2;
    k.clear(drum(r * 0.9, r, H * 0.8, 0, H * 0.4, 0, 8));
    k.add(drum(r * 0.8, r * 0.8, H * 0.5, 0, H * 0.3, 0, 8), WHITE);
    k.add(drum(r * 0.9, r * 0.9, H * 0.2, 0, H * 0.9, 0, 8), METAL);
  },
  shard(k, W, H, c) {
    for (let i = 0; i < 3; i += 1) {
      const g = new THREE.TetrahedronGeometry(W * 0.3, 0).scale(1, 0.3, 0.6).rotateY(i * 2.1).translate((i - 1) * W * 0.25, H * 0.35, (i % 2 ? 1 : -1) * W * 0.15);
      k.clear(g, c);
    }
  },
  mirror(k, W, H, c) {
    // A hand mirror lying face up: a round frame and a handle, inside its disc.
    const r = W / 2;
    k.add(drum(r * 0.72, r * 0.72, H * 0.6, -r * 0.25, H * 0.3, 0, 16), BRASS);
    k.clear(drum(r * 0.64, r * 0.64, H * 0.2, -r * 0.25, H * 0.65, 0, 16), c);
    k.add(block(r * 0.75, H * 0.5, r * 0.18, r * 0.55, H * 0.25, 0), BRASS);
  },
  glassrack(k, W, H, c) {
    k.add(block(W, H * 0.15, W, 0, H * 0.075, 0), 0x4c5a66);
    for (const s of [-1, 1]) k.add(block(W, H * 0.9, 0.02, 0, H * 0.45, s * (W / 2 - 0.01)), 0x4c5a66);
    const n = 4;
    for (let i = 0; i < n; i += 1) for (let j = 0; j < n; j += 1) {
      k.clear(drum(W / (n * 2.4), W / (n * 2.8), H * 0.7, -W / 2 + (W / n) * (i + 0.5), H * 0.5, -W / 2 + (W / n) * (j + 0.5), 8), c);
    }
  },
  // --- small and pocketable -------------------------------------------------------
  band(k, W, H, c) {
    k.add(flatRing(W * 0.38, Math.min(H, W) * 0.12, 0, H / 2, 0), c);
  },
  popper(k, W, H, c) {
    k.add(cone(W * 0.45, H * 0.85, 0, H * 0.425, 0, 10), c);
    k.add(drum(W * 0.46, W * 0.46, H * 0.08, 0, H * 0.84, 0, 10), 0xe0b020);
    k.add(rod(W * 0.03, [0, H * 0.02, 0], [W * 0.3, 0.001, W * 0.3], 4), WHITE);
  },
  ice(k, W, H) {
    for (let i = 0; i < 5; i += 1) {
      const s = W * 0.32;
      k.clear(block(s, s, s, ((i % 3) - 1) * W * 0.28, s / 2 + (i > 2 ? s * 0.8 : 0), (i % 2 ? 1 : -1) * W * 0.18).rotateY(i * 0.7), 0xd8eef4);
    }
  },
  shrimp(k, W, H, c) {
    k.add(flatRing(W * 0.3, Math.min(H, W) * 0.22, 0, H * 0.45, 0, { arc: Math.PI * 1.3, segments: 10 }), c);
    k.add(block(W * 0.4, H * 0.3, W * 0.2, 0, H * 0.15, 0), 0xe6e0d0);
  },
  note(k, W, H, c) {
    k.add(block(W * 0.95, Math.max(H, 0.002), W * 0.7, 0, Math.max(H, 0.002) / 2, 0), 0xf4f0e2);
    k.add(block(W * 0.6, 0.001, W * 0.08, 0, Math.max(H, 0.002) + 0.0006, 0), c);
  },
  napkin(k, W, H, c) {
    const g = new THREE.CylinderGeometry(W * 0.5, W * 0.5, H, 3).translate(0, H / 2, 0);
    k.add(g, c);
    k.add(block(W * 0.5, H * 0.3, W * 0.05, 0, H * 1.05, -W * 0.1), c);
  },
  sugar(k, W, H, c) {
    k.add(block(W * 0.9, H * 0.6, W * 0.55, 0, H * 0.3, 0), c);
    k.add(block(W * 0.5, H * 0.62, W * 0.3, 0, H * 0.31, 0), 0x3a6aa0);
  },
  teaspoon(k, W, H) {
    k.add(rod(H * 0.18, [-W * 0.45, H * 0.4, 0], [W * 0.15, H * 0.25, 0], 5), METAL);
    k.add(ball(W * 0.14, W * 0.3, H * 0.3, 0, 1.4, 0.35, 1, 8), METAL);
  },
  matchbook(k, W, H, c) {
    k.add(block(W * 0.9, H * 0.8, W * 0.55, 0, H * 0.4, 0), c);
    k.add(block(W * 0.2, H * 0.82, W * 0.56, W * 0.34, H * 0.41, 0), 0x3a2a24);
  },
  biro(k, W, H, c) {
    const r = Math.max(H * 0.45, 0.004);
    k.add(rod(r, [-W * 0.48, r, 0], [W * 0.3, r, 0], 6), 0xf0f0f0);
    k.add(rod(r * 1.05, [W * 0.28, r, 0], [W * 0.48, r, 0], 6), c);
    k.add(block(W * 0.18, r * 0.4, r * 0.5, W * 0.36, r * 2, 0), c);
  },
  keycard(k, W, H, c) {
    k.add(block(W * 0.9, Math.max(H, 0.002), W * 0.56, 0, Math.max(H, 0.002) / 2, 0), c);
    k.add(block(W * 0.9, 0.0008, W * 0.1, 0, Math.max(H, 0.002) + 0.0004, W * 0.16), BLACK);
  },
  coaster(k, W, H, c) {
    k.add(drum(W / 2, W / 2, H, 0, H / 2, 0, 14), c);
    k.add(drum(W * 0.4, W * 0.4, H * 0.1, 0, H * 1.02, 0, 14), 0xe8e0c8);
  },
  menu(k, W, H, c) {
    // A folded card, lying open a crack.
    const leaf = (s) => block(W * 0.9, 0.003, W * 0.32, 0, 0, 0).rotateX(s * 0.06).translate(0, H * 0.5, s * W * 0.16);
    k.add(leaf(1), c);
    k.add(leaf(-1), 0x5a3d28);
  },
  remote(k, W, H, c) {
    k.add(block(W * 0.95, H * 0.6, W * 0.35, 0, H * 0.3, 0), c);
    for (let i = 0; i < 6; i += 1) k.add(block(W * 0.08, H * 0.12, W * 0.06, -W * 0.3 + (i % 3) * W * 0.12, H * 0.62, (i < 3 ? -1 : 1) * W * 0.06), i === 0 ? 0xb02a20 : 0x8d94a6);
  },
  soap(k, W, H, c) {
    k.add(ball(W * 0.5, 0, H * 0.5, 0, 1, (H / W) * 0.9, 0.65, 10), c);
  },
  // --- food ------------------------------------------------------------------------
  breadroll(k, W, H, c) {
    k.add(ball(W / 2, 0, H * 0.5, 0, 1, H / W, 0.8, 10), c);
    k.add(block(W * 0.6, H * 0.06, W * 0.06, 0, H * 0.93, 0), 0x6a4020);
  },
  lemon(k, W, H, c) {
    k.add(ball(W * 0.42, 0, W * 0.42, 0, 1.2, 1, 1, 10), c);
    for (const s of [-1, 1]) k.add(cone(W * 0.07, W * 0.1, s * W * 0.52, W * 0.42, 0, 6, { spin: -s * Math.PI / 2 }), c);
  },
  apple(k, W, H, c) {
    k.add(ball(W / 2, 0, W * 0.46, 0, 1, 0.92, 1, 12), c);
    k.add(rod(W * 0.03, [0, W * 0.85, 0], [W * 0.05, W, 0], 4), 0x5a3a20);
    k.add(ball(W * 0.12, W * 0.12, W * 0.96, 0, 1, 0.25, 0.6, 6), 0x4a7a3a);
  },
  orange(k, W, H, c) {
    k.add(ball(W / 2, 0, W * 0.48, 0, 1, 0.96, 1, 12), c);
    k.add(ball(W * 0.06, 0, W * 0.95, 0, 1, 0.4, 1, 6), 0x6a5020);
  },
  // --- the table ----------------------------------------------------------------------
  coffeecup(k, W, H, c) {
    const r = W / 2;
    k.add(drum(r, r, H * 0.08, 0, H * 0.04, 0, 14), c);
    k.add(lathe([[r * 0.45, H * 0.08], [r * 0.7, H * 0.2], [r * 0.72, H], [r * 0.66, H], [r * 0.62, H * 0.25], [0, H * 0.25]]), c);
    k.add(ring(H * 0.18, r * 0.05, r * 0.72, H * 0.6, 0, { arc: Math.PI, spin: -Math.PI / 2 }), c);
    k.add(drum(r * 0.64, r * 0.64, 0.004, 0, H * 0.85, 0, 12), 0x3a2410);
  },
  candle(k, W, H, c) {
    const r = W / 2;
    k.clear(lathe([[r * 0.8, 0], [r, H * 0.1], [r, H * 0.9], [r * 0.9, H * 0.9], [0, H * 0.06]]));
    k.add(drum(r * 0.8, r * 0.8, H * 0.55, 0, H * 0.3, 0, 10), c);
    k.add(rod(r * 0.05, [0, H * 0.55, 0], [0, H * 0.7, 0], 4), BLACK);
  },
  budvase(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.6, 0], [r, H * 0.2], [r * 0.5, H * 0.45], [r * 0.25, H * 0.6], [r * 0.3, H * 0.65], [0, H * 0.65]]), c);
    k.add(rod(r * 0.06, [0, H * 0.6, 0], [r * 0.2, H * 0.92, 0], 4), 0x4a7a3a);
    k.add(ball(r * 0.35, r * 0.2, H * 0.94, 0, 1, 0.8, 1, 8), 0xd0506a);
  },
  sideplate(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.55, 0], [r * 0.6, H * 0.35], [r, H * 0.85], [r, H], [r * 0.95, H], [r * 0.55, H * 0.5], [0, H * 0.5]], 18), c);
    k.add(flatRing(r * 0.93, H * 0.08, 0, H * 0.95, 0, { segments: 18 }), BRASS);
  },
  platter(k, W, H, c) {
    BUILD.sideplate(k, W, H, c);
  },
  ashtray(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.8, 0], [r, H * 0.2], [r, H], [r * 0.8, H], [r * 0.7, H * 0.3], [0, H * 0.3]], 12), c);
    for (let i = 0; i < 3; i += 1) k.add(block(r * 0.3, H * 0.2, r * 0.12, r * 0.9, H * 0.95, 0).rotateY((i * Math.PI * 2) / 3), 0x3a3e44);
  },
  icebucket(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.7, 0], [r * 0.75, H * 0.05], [r * 0.95, H * 0.95], [r, H], [r * 0.92, H], [r * 0.7, H * 0.1], [0, H * 0.1]], 14), c);
    for (const s of [-1, 1]) k.add(ring(r * 0.15, r * 0.03, s * r * 0.95, H * 0.8, 0, { arc: Math.PI, yaw: Math.PI / 2, segments: 8 }), METAL);
    for (let i = 0; i < 5; i += 1) k.clear(block(r * 0.3, r * 0.3, r * 0.3, ((i % 3) - 1) * r * 0.35, H * 0.88, (i % 2 ? 1 : -1) * r * 0.25).rotateY(i), 0xd8eef4);
  },
  coffeepot(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.7, 0], [r * 0.85, H * 0.1], [r * 0.85, H * 0.7], [r * 0.55, H * 0.85], [r * 0.5, H], [0, H]], 14), c);
    k.add(ring(H * 0.22, r * 0.07, -r * 0.8, H * 0.5, 0, { arc: Math.PI, spin: Math.PI / 2 }), BLACK);
    k.add(cone(r * 0.14, r * 0.5, r * 0.9, H * 0.78, 0, 6, { spin: -0.9 }), c);
    k.add(ball(r * 0.14, 0, H, 0, 1, 0.6, 1, 6), BLACK);
  },
  coffeeurn(k, W, H, c) {
    const r = W / 2;
    k.add(drum(r * 0.85, r * 0.9, H * 0.75, 0, H * 0.5, 0, 14), c);
    k.add(lathe([[r * 0.85, H * 0.875], [r * 0.6, H * 0.95], [r * 0.15, H], [0, H]], 14), c);
    for (let i = 0; i < 3; i += 1) k.add(rod(r * 0.05, [0, H * 0.12, 0], [0, 0, 0], 4).translate(Math.cos(i * 2.1) * r * 0.7, 0, Math.sin(i * 2.1) * r * 0.7), BLACK);
    k.add(block(r * 0.2, r * 0.12, r * 0.25, r * 0.9, H * 0.2, 0), BLACK);
    k.add(block(r * 0.06, H * 0.4, r * 0.06, r * 0.88, H * 0.5, r * 0.3), GLASS);
  },
  tray(k, W, H, c) {
    const w = W;
    const d = W * 0.72;
    k.add(block(w, H * 0.2, d, 0, H * 0.1, 0), c);
    for (const s of [-1, 1]) {
      k.add(block(w, H * 0.35, 0.015, 0, H * 0.3, s * (d / 2 - 0.008)), c);
      k.add(block(0.015, H * 0.35, d, s * (w / 2 - 0.008), H * 0.3, 0), c);
    }
    k.add(ball(Math.min(w, d) * 0.28, 0, H * 0.2, 0, 1, Math.min(1, (H * 0.8) / (Math.min(w, d) * 0.28)), 1, 12), METAL);
  },
  chafer(k, W, H, c) {
    const w = W;
    const d = W * 0.62;
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) k.add(rod(0.008, [sx * w * 0.42, 0, sz * d * 0.42], [sx * w * 0.42, H * 0.5, sz * d * 0.42], 4), METAL);
    k.add(block(w * 0.9, H * 0.3, d * 0.9, 0, H * 0.55, 0), c);
    k.add(ball(w * 0.45, 0, H * 0.7, 0, 1, Math.min(0.6, (H * 0.3) / (w * 0.45)), d / w, 12), c);
    k.add(block(w * 0.12, H * 0.05, d * 0.1, 0, H * 0.98, 0), BLACK);
  },
  // --- kit ------------------------------------------------------------------------------
  towel(k, W, H, c) {
    k.add(block(W * 0.9, H * 0.7, W * 0.6, 0, H * 0.35, 0), c);
    k.add(drum(H * 0.2, H * 0.2, W * 0.9, 0, 0, 0, 8).rotateZ(Math.PI / 2).translate(0, H * 0.72, W * 0.25), c);
  },
  clipboard(k, W, H, c) {
    k.add(block(W * 0.7, H * 0.5, W * 0.95, 0, H * 0.25, 0), c);
    k.add(block(W * 0.62, H * 0.2, W * 0.8, 0, H * 0.6, W * 0.05), 0xf4f0e2);
    k.add(block(W * 0.3, H * 0.5, W * 0.1, 0, H * 0.75, -W * 0.42), METAL);
  },
  tape(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.55, 0], [r, 0], [r, H], [r * 0.55, H], [r * 0.55, 0]], 16), c);
  },
  radio(k, W, H, c) {
    const d = W * 0.4;
    k.add(block(W, H * 0.8, d, 0, H * 0.4, 0), c);
    k.add(block(W * 0.4, H * 0.5, 0.005, -W * 0.22, H * 0.4, d / 2 + 0.002), DARK);
    k.add(block(W * 0.35, H * 0.14, 0.005, W * 0.24, H * 0.6, d / 2 + 0.002), 0xd8c89a);
    k.add(drum(H * 0.07, H * 0.07, 0.02, 0, 0, 0, 8).rotateX(Math.PI / 2).translate(W * 0.3, H * 0.3, d / 2 + 0.01), DARK);
    for (const s of [-1, 1]) k.add(block(0.02, H * 0.2, 0.02, s * W * 0.38, H * 0.9, 0), DARK);
    k.add(block(W * 0.78, 0.02, 0.02, 0, H, 0), DARK);
    k.add(rod(0.004, [W * 0.4, H * 0.8, 0], [W * 0.5, H, -d * 0.4], 4), METAL);
  },
  broom(k, W, H, c) {
    k.add(rod(W * 0.05, [0, H * 0.22, 0], [0, H, 0], 6), c);
    k.add(block(W * 0.9, H * 0.08, W * 0.25, 0, H * 0.2, 0), 0x3a3a3c);
    k.add(block(W * 0.95, H * 0.16, W * 0.22, 0, H * 0.08, 0), 0xc8a45a);
  },
  mop(k, W, H, c) {
    k.add(rod(W * 0.05, [0, H * 0.18, 0], [0, H, 0], 6), METAL);
    k.add(block(W * 0.4, H * 0.04, W * 0.2, 0, H * 0.18, 0), DARK);
    for (let i = 0; i < 10; i += 1) {
      const a = (i / 10) * Math.PI * 2;
      k.add(rod(W * 0.035, [0, H * 0.17, 0], [Math.cos(a) * W * 0.42, 0.01, Math.sin(a) * W * 0.42], 4), 0xe6e0d0);
    }
  },
  firstaid(k, W, H, c) {
    const d = W * 0.45;
    k.add(block(W, H * 0.85, d, 0, H * 0.425, 0), c);
    k.add(block(W * 0.3, H * 0.08, d * 0.06, 0, H * 0.425, d / 2 + 0.003), WHITE);
    k.add(block(W * 0.09, H * 0.28, d * 0.06, 0, H * 0.425, d / 2 + 0.004), WHITE);
    k.add(block(W * 0.3, H * 0.15, d * 0.15, 0, H * 0.92, 0), DARK);
  },
  bucket(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.75, H * 0.08], [r * 0.95, H * 0.75], [r, H * 0.78], [r * 0.9, H * 0.78], [r * 0.7, H * 0.15], [0, H * 0.15]], 14), c);
    k.add(block(r * 0.9, H * 0.3, r * 0.5, r * 0.3, H * 0.9, 0), 0x3a3e44);
    for (let i = 0; i < 4; i += 1) k.add(drum(r * 0.1, r * 0.1, r * 0.06, 0, 0, 0, 8).rotateZ(Math.PI / 2).translate(Math.cos(i * 1.57 + 0.78) * r * 0.7, r * 0.1, Math.sin(i * 1.57 + 0.78) * r * 0.7), BLACK);
    k.add(drum(r * 0.8, r * 0.8, 0.005, 0, H * 0.6, 0, 12), 0x8aa0a8);
  },
  bustub(k, W, H, c) {
    const w = W;
    const d = W * 0.7;
    k.add(block(w * 0.94, H * 0.1, d * 0.94, 0, H * 0.05, 0), c);
    for (const s of [-1, 1]) {
      k.add(block(w, H, 0.015, 0, H / 2, s * (d / 2 - 0.008)), c);
      k.add(block(0.015, H, d, s * (w / 2 - 0.008), H / 2, 0), c);
    }
    k.add(drum(w * 0.1, w * 0.08, H * 0.4, -w * 0.2, H * 0.3, 0, 8), 0xd7d2c6);
    k.add(drum(w * 0.14, w * 0.14, H * 0.05, w * 0.2, H * 0.15, d * 0.1, 12), 0xe2ded2);
  },
  foldingsign(k, W, H, c) {
    const leaf = (s) => block(W * 0.9, H * 0.98, 0.012, 0, 0, 0).rotateX(s * 0.18).translate(0, H * 0.49, s * W * 0.09);
    k.add(leaf(1), c);
    k.add(leaf(-1), c);
    // A figure slipping, painted on each face: a black mark.
    for (const s of [-1, 1]) k.add(block(W * 0.3, H * 0.3, 0.004, 0, 0, 0).rotateX(s * 0.18).translate(0, H * 0.55, s * (W * 0.09 + 0.008)), BLACK);
  },
  wateringcan(k, W, H, c) {
    const r = W * 0.3;
    k.add(drum(r, r, H * 0.7, -W * 0.1, H * 0.35, 0, 12), c);
    k.add(rod(W * 0.035, [-W * 0.1 + r * 0.8, H * 0.2, 0], [W * 0.48, H * 0.85, 0], 6), c);
    k.add(drum(W * 0.06, W * 0.04, W * 0.06, W * 0.48, H * 0.88, 0, 8), c);
    k.add(ring(H * 0.25, W * 0.025, -W * 0.1, H * 0.72, 0, { arc: Math.PI }), c);
  },
  toolbox(k, W, H, c) {
    const d = W * 0.45;
    k.add(block(W, H * 0.7, d, 0, H * 0.35, 0), c);
    k.add(block(W * 1.01, H * 0.1, d * 1.01, 0, H * 0.72, 0), 0x6a3a22);
    k.add(block(W * 0.5, H * 0.06, 0.03, 0, H * 0.95, 0), DARK);
    for (const s of [-1, 1]) k.add(block(0.02, H * 0.2, 0.03, s * W * 0.25, H * 0.85, 0), DARK);
    k.add(block(W * 0.08, H * 0.1, 0.01, 0, H * 0.62, d / 2 + 0.005), METAL);
  },
  icebag(k, W, H, c) {
    // A clear bag, full, lying on its side, with the tie at one end.
    k.clear(ball(W * 0.46, 0, H * 0.42, 0, 1, (H * 0.84) / (W * 0.92), 0.62, 10), c);
    k.add(block(W * 0.08, H * 0.14, W * 0.12, W * 0.44, H * 0.42, 0), 0x3a78b0);
  },
  sandbag(k, W, H, c) {
    k.add(ball(W / 2, 0, H / 2, 0, 1, H / W, 0.6, 10), c);
    k.add(block(W * 0.1, H * 0.4, W * 0.62, W * 0.42, H * 0.5, 0), 0x6a5a38);
  },
  extinguisher(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.95, 0], [r, H * 0.02], [r, H * 0.7], [r * 0.7, H * 0.8], [r * 0.3, H * 0.84], [0, H * 0.84]], 14), c);
    k.add(drum(r * 0.25, r * 0.3, H * 0.06, 0, H * 0.87, 0, 8), METAL);
    k.add(block(r * 1.3, H * 0.03, r * 0.25, r * 0.3, H * 0.93, 0), BLACK);
    k.add(block(r * 1.1, H * 0.03, r * 0.22, r * 0.2, H * 0.98, 0).rotateZ(0), BLACK);
    k.add(rod(r * 0.1, [r * 0.3, H * 0.86, 0], [r * 0.95, H * 0.5, r * 0.3], 5), BLACK);
    k.add(block(r * 0.8, H * 0.12, 0.004, 0, H * 0.45, r + 0.002), WHITE);
  },
  plant(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.45, 0], [r * 0.6, H * 0.3], [r * 0.62, H * 0.32], [0, H * 0.32]], 12), 0x8a5a3a);
    k.add(rod(r * 0.05, [0, H * 0.3, 0], [0, H * 0.6, 0], 5), 0x5a3a20);
    for (let i = 0; i < 6; i += 1) {
      const a = i * 1.05;
      k.add(ball(r * 0.45, Math.cos(a) * r * 0.45, H * (0.6 + (i % 3) * 0.1), Math.sin(a) * r * 0.45, 1, 1.1, 1, 7), c);
    }
    k.add(ball(r * 0.5, 0, H * 0.86, 0, 1, 0.9, 1, 7), c);
  },
  cinderblock(k, W, H, c) {
    const d = W * 0.5;
    const t = W * 0.12;
    for (const s of [-1, 1]) k.add(block(W, H, t, 0, H / 2, s * (d / 2 - t / 2)), c);
    for (const x of [-1, 0, 1]) k.add(block(t, H, d - t * 2, x * (W / 2 - t / 2), H / 2, 0), c);
  },
  luggage(k, W, H, c) {
    const d = W * 0.4;
    k.add(block(W * 0.95, H * 0.88, d, 0, H * 0.48, 0), c);
    for (let i = 0; i < 3; i += 1) k.add(block(W * 0.96, H * 0.02, d * 1.02, 0, H * 0.2 + i * H * 0.28, 0), 0x2a2420);
    for (const s of [-1, 1]) k.add(rod(W * 0.02, [s * W * 0.2, H * 0.9, -d * 0.4], [s * W * 0.2, H, -d * 0.4], 4), METAL);
    k.add(block(W * 0.44, H * 0.03, W * 0.05, 0, H, -d * 0.4), DARK);
    for (const s of [-1, 1]) k.add(drum(H * 0.04, H * 0.04, W * 0.05, 0, 0, 0, 8).rotateZ(Math.PI / 2).translate(s * W * 0.4, H * 0.04, -d * 0.35), BLACK);
    k.add(block(W * 0.3, H * 0.04, W * 0.04, 0, H * 0.7, d / 2 + 0.01), 0x2a2420);
  },
  icechest(k, W, H, c) {
    const d = W * 0.55;
    k.add(block(W, H * 0.78, d, 0, H * 0.39, 0), c);
    k.add(block(W * 1.02, H * 0.18, d * 1.02, 0, H * 0.87, 0), WHITE);
    for (const s of [-1, 1]) k.add(block(0.04, H * 0.1, d * 0.4, s * (W / 2 + 0.02), H * 0.6, 0), DARK);
  },
  keg(k, W, H, c) {
    const r = W / 2;
    k.add(lathe([[r * 0.85, 0], [r * 0.95, H * 0.08], [r, H * 0.5], [r * 0.95, H * 0.92], [r * 0.85, H], [0, H]], 16), c);
    for (const y of [0.08, 0.3, 0.7, 0.92]) k.add(flatRing(r * (y === 0.3 || y === 0.7 ? 1 : 0.96), r * 0.04, 0, H * y, 0, { segments: 16 }), 0x6a7078);
    k.add(drum(r * 0.2, r * 0.2, H * 0.04, 0, H * 0.98, 0, 8), BLACK);
  },
  buffer(k, W, H, c) {
    const r = W / 2;
    k.add(drum(r, r, H * 0.08, 0, H * 0.06, 0, 16), DARK);
    k.add(drum(r * 0.9, r * 0.95, H * 0.12, 0, H * 0.14, 0, 16), c);
    k.add(drum(r * 0.35, r * 0.4, H * 0.18, 0, H * 0.28, 0, 12), c);
    k.add(rod(r * 0.05, [0, H * 0.3, 0], [0, H * 0.95, -r * 0.8], 6), METAL);
    k.add(block(r * 1.2, H * 0.03, r * 0.1, 0, H * 0.96, -r * 0.82), BLACK);
  },
  // --- the desert -------------------------------------------------------------------------
  scorpion(k, W, H, c) {
    for (let i = 0; i < 4; i += 1) k.add(ball(W * 0.1, -W * 0.05 + i * W * 0.1, H * 0.3, 0, 1, 0.5, 0.8, 6), c);
    for (let i = 0; i < 4; i += 1) k.add(ball(W * 0.05, -W * 0.15 - i * W * 0.05, H * (0.4 + i * 0.18), 0, 1, 1, 1, 5), c);
    k.add(cone(W * 0.03, W * 0.08, -W * 0.33, H * 0.95, 0, 5, { spin: 1.2 }), 0x3a2a14);
    for (const s of [-1, 1]) {
      k.add(rod(W * 0.02, [W * 0.3, H * 0.3, s * W * 0.05], [W * 0.45, H * 0.3, s * W * 0.2], 4), c);
      k.add(ball(W * 0.06, W * 0.47, H * 0.3, s * W * 0.22, 1.4, 0.6, 0.8, 5), c);
      for (let j = 0; j < 4; j += 1) k.add(rod(W * 0.012, [W * (0.05 + j * 0.06), H * 0.3, 0], [W * (j * 0.07), 0.001, s * W * 0.3], 3), c);
    }
  },
  lizard(k, W, H, c) {
    k.add(ball(W * 0.18, 0, H * 0.35, 0, 1.6, 0.5, 0.6, 8), c);
    k.add(ball(W * 0.08, W * 0.32, H * 0.4, 0, 1.4, 0.7, 0.8, 6), c);
    k.add(cone(W * 0.05, W * 0.45, -W * 0.5, H * 0.3, 0, 5, { spin: Math.PI / 2 }), c);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) k.add(rod(W * 0.02, [sx * W * 0.12, H * 0.3, sz * W * 0.05], [sx * W * 0.18, 0.001, sz * W * 0.22], 3), c);
  },
  toad(k, W, H, c) {
    k.add(ball(W * 0.4, 0, H * 0.45, 0, 1, 0.8, 1, 10), c);
    for (const s of [-1, 1]) k.add(ball(W * 0.09, W * 0.25, H * 0.85, s * W * 0.15, 1, 1, 1, 6), 0xc8a030);
    for (const s of [-1, 1]) k.add(ball(W * 0.14, -W * 0.2, H * 0.15, s * W * 0.32, 1.4, 0.5, 1, 6), c);
  },
  quail(k, W, H, c) {
    k.add(ball(W * 0.4, 0, H * 0.4, 0, 1.2, 0.9, 1, 10), c);
    k.add(ball(W * 0.2, W * 0.35, H * 0.72, 0, 1, 1, 1, 8), 0x5a4a3a);
    k.add(cone(W * 0.05, W * 0.12, W * 0.52, H * 0.72, 0, 4, { spin: -Math.PI / 2 }), BLACK);
    k.add(ball(W * 0.06, W * 0.38, H * 0.95, 0, 0.8, 1.4, 0.6, 6), BLACK);
    for (const s of [-1, 1]) k.add(rod(W * 0.02, [0, H * 0.1, s * W * 0.1], [0, 0, s * W * 0.1], 3), 0x8a6a3a);
  },
  bat(k, W, H, c) {
    k.add(ball(W * 0.14, 0, H * 0.5, 0, 1, 1, 1.4, 8), c);
    for (const s of [-1, 1]) k.add(new THREE.CylinderGeometry(W * 0.45, W * 0.45, 0.004, 3, 1, false, 0, Math.PI).rotateY(s > 0 ? 0 : Math.PI).translate(0, H * 0.55, s * W * 0.05), c);
    for (const s of [-1, 1]) k.add(cone(W * 0.04, W * 0.1, W * 0.08, H * 0.85, s * W * 0.05, 4), c);
  },
  tarantula(k, W, H, c) {
    k.add(ball(W * 0.14, W * 0.1, H * 0.45, 0, 1, 0.7, 1, 8), c);
    k.add(ball(W * 0.18, -W * 0.15, H * 0.5, 0, 1.2, 0.8, 1, 8), 0x3a2a1a);
    for (const s of [-1, 1]) for (let j = 0; j < 4; j += 1) {
      const x = W * (0.2 - j * 0.08);
      k.add(rod(W * 0.02, [x, H * 0.45, s * W * 0.08], [x + W * 0.05, H * 0.9, s * W * 0.25], 3), c);
      k.add(rod(W * 0.02, [x + W * 0.05, H * 0.9, s * W * 0.25], [x + W * 0.1, 0.001, s * W * 0.45], 3), c);
    }
  },
  roadrunner(k, W, H, c) {
    k.add(ball(W * 0.3, 0, H * 0.5, 0, 1.3, 0.8, 0.8, 10), c);
    k.add(ball(W * 0.14, W * 0.35, H * 0.8, 0, 1, 1, 0.9, 8), c);
    k.add(cone(W * 0.04, W * 0.2, W * 0.55, H * 0.8, 0, 4, { spin: -Math.PI / 2 }), DARK);
    k.add(block(W * 0.4, W * 0.04, W * 0.12, 0, 0, 0).rotateZ(-0.4).translate(-W * 0.42, H * 0.6, 0), DARK);
    for (const s of [-1, 1]) k.add(rod(W * 0.02, [0, H * 0.35, s * W * 0.08], [W * 0.05, 0, s * W * 0.1], 3), 0x6a6a5a);
  },
  rattlesnake(k, W, H, c) {
    const r = W / 2;
    for (let i = 0; i < 3; i += 1) k.add(flatRing(r * (0.75 - i * 0.2), H * 0.2, 0, H * (0.2 + i * 0.25), 0), c);
    k.add(ball(H * 0.28, r * 0.1, H * 0.95, 0, 1.4, 0.8, 1, 6), c);
    k.add(block(r * 0.2, H * 0.3, H * 0.2, -r * 0.2, H * 0.9, 0), 0xd8c89a);
  },
};

const solidMaterials = new Map();

/** The kinds with a model. */
export const MODELLED_PROPS = Object.freeze(Object.keys(BUILD));

/**
 * One kind's geometry at its size: groups [solid, glass], colours in the
 * vertices. `W` is the width (the row's diameter) and `H` its height, in metres.
 */
export function propGeometry(kind, W, H, colour = 0xcccccc) {
  const build = BUILD[kind];
  if (!build) return null;
  const k = kit();
  build(k, W, H, colour);
  const paint = (list) =>
    list.map(([geometry, c]) => {
      const g = clean(geometry);
      const col = new THREE.Color(c);
      const n = g.attributes.position.count;
      const data = new Float32Array(n * 3);
      for (let i = 0; i < n; i += 1) {
        data[i * 3] = col.r;
        data[i * 3 + 1] = col.g;
        data[i * 3 + 2] = col.b;
      }
      g.setAttribute('color', new THREE.BufferAttribute(data, 3));
      return g;
    });
  const solid = paint(k.solid);
  const glass = paint(k.glass);
  const parts = [];
  const groups = [];
  let start = 0;
  for (const [index, list] of [solid, glass].entries()) {
    if (!list.length) continue;
    const g = mergeGeometries(list);
    for (const piece of list) piece.dispose();
    parts.push(g);
    groups.push({ start, count: g.attributes.position.count, materialIndex: index });
    start += g.attributes.position.count;
  }
  if (!parts.length) return null;
  const geometry = parts.length > 1 ? mergeGeometries(parts) : parts[0];
  if (parts.length > 1) for (const p of parts) p.dispose();
  geometry.clearGroups();
  for (const g of groups) geometry.addGroup(g.start, g.count, g.materialIndex);
  // Inside its box, whatever the builder did: a model that runs over is shrunk
  // evenly until it fits, stood on the floor and centred. The box is the one
  // the game collides, and a thing drawn bigger than it is a thing that lies.
  geometry.computeBoundingBox();
  const b = geometry.boundingBox;
  const fit = Math.min(1, W / Math.max(1e-6, 2 * Math.max(b.max.x, -b.min.x)), W / Math.max(1e-6, 2 * Math.max(b.max.z, -b.min.z)), H / Math.max(1e-6, b.max.y - Math.min(0, b.min.y)));
  if (fit < 1) geometry.scale(fit, fit, fit);
  geometry.computeBoundingBox();
  if (geometry.boundingBox.min.y < 0) geometry.translate(0, -geometry.boundingBox.min.y, 0);
  geometry.computeBoundingSphere();
  return geometry;
}

/** The two materials every prop is drawn with: vertex-coloured solid, and glass. */
export function propMaterials() {
  if (!solidMaterials.size) {
    solidMaterials.set('solid', new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7, metalness: 0.05 }));
    solidMaterials.set(
      'glass',
      new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.08, metalness: 0.1, transparent: true, opacity: 0.5, depthWrite: false })
    );
  }
  return [solidMaterials.get('solid'), solidMaterials.get('glass')];
}
